import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

import { Bell, PencilRuler, Search, Trash2 } from "lucide-react";

type CatalogProduct = {
  id: string;
  sku: string;
  name: string;
  description: string;
  category: string;
  priceTry: number;
  active: boolean;
  tags: string[];
  imageUrl?: string;
  imageUrls?: string[];
};

type CatalogResponse = {
  ok: true;
  packages: unknown[];
  products: CatalogProduct[];
  meditations: unknown[];
  templates: unknown[];
  settings: unknown;
};

function formatTry(amount: number) {
  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ₺`;
  }
}

function normalizeSearchText(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u");
}

function normalizeCategoryParent(value: string) {
  const key = normalizeSearchText(value).replace(/[^a-z0-9-]/g, "");
  if (["stone", "tas", "dogal-tas", "dogaltas"].includes(key)) return "stone";
  if (["oil", "yag", "ucucu-yag", "ucucuyag", "aroma-yag"].includes(key)) return "oil";
  if (["bundle", "paket", "set", "kombin"].includes(key)) return "bundle";
  if (["other", "diger"].includes(key)) return "other";
  return key || "other";
}

function humanizeCategoryPart(value: string) {
  const raw = value.trim();
  if (!raw) return "";
  const normalized = normalizeCategoryParent(raw);

  if (normalized === "stone") return "Taş";
  if (normalized === "oil") return "Yağ";
  if (normalized === "bundle") return "Paket";
  if (normalized === "other") return "Diğer";

  const withSpaces = raw.replace(/[-_]+/g, " ").trim();
  return withSpaces.charAt(0).toLocaleUpperCase("tr-TR") + withSpaces.slice(1);
}

function getPrimaryCategory(category: string) {
  const first = String(category ?? "")
    .split(/[/>]/)
    .map((part) => part.trim())
    .find(Boolean);

  return normalizeCategoryParent(first ?? "");
}

function formatCategoryLabel(category: string) {
  const parts = String(category ?? "")
    .split(/[/>]/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) return "Diğer";
  return parts.map(humanizeCategoryPart).join(" / ");
}

async function fetchCatalog(): Promise<CatalogResponse> {
  const res = await adminFetch("/api/admin/numeroloji/catalog");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Katalog alınamadı.");
  }
  return json as CatalogResponse;
}

async function saveProduct(payload: Partial<CatalogProduct> & { sku: string; name: string }) {
  const res = await adminFetch("/api/admin/numeroloji/products", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Ürün kaydedilemedi.");
  }
  return json as { ok: true; item: CatalogProduct };
}

async function deleteProduct(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/products/${encodeURIComponent(id)}/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Ürün silinemedi.");
  }
  return json as { ok: true; deleted: boolean };
}

export default function AdminNumerolojiProducts() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const catalogQuery = useQuery({
    queryKey: ["admin-numeroloji-catalog"],
    queryFn: fetchCatalog,
    enabled: authenticated,
    staleTime: 30_000,
  });

  const products = useMemo(() => catalogQuery.data?.products ?? [], [catalogQuery.data]);

  const productSaveMutation = useMutation({
    mutationFn: (payload: any) => saveProduct(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const productDeleteMutation = useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const [productSearch, setProductSearch] = useState("");
  const [productFilter, setProductFilter] = useState<"all" | "active" | "inactive">("all");
  const [productSort, setProductSort] = useState<"default" | "price-asc" | "price-desc" | "name-asc">("default");

  const visibleProducts = useMemo(() => {
    const q = normalizeSearchText(productSearch);

    let rows = products;

    if (q) {
      rows = rows.filter((p) => {
        const categoryLabel = formatCategoryLabel(p.category);
        const hay = normalizeSearchText(`${p.name} ${p.sku} ${p.category} ${categoryLabel} ${(p.tags ?? []).join(" ")}`);
        return hay.includes(q);
      });
    }

    if (productFilter === "active") {
      rows = rows.filter((p) => p.active);
    } else if (productFilter === "inactive") {
      rows = rows.filter((p) => !p.active);
    }

    const sorted = [...rows];
    if (productSort === "price-asc") {
      sorted.sort((a, b) => Number(a.priceTry || 0) - Number(b.priceTry || 0));
    } else if (productSort === "price-desc") {
      sorted.sort((a, b) => Number(b.priceTry || 0) - Number(a.priceTry || 0));
    } else if (productSort === "name-asc") {
      sorted.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "tr"));
    }

    return sorted;
  }, [productFilter, productSearch, productSort, products]);

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
                mode="links"
                isDark={isDark}
                onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
              />
              <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">Ürün Yönetimi</div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                aria-label="Bildirimler"
              >
                <Bell className="h-4 w-4" />
              </button>

              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          {catalogQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Yükleniyor...</div>
          ) : catalogQuery.isError ? (
            <div className="text-sm text-destructive">{(catalogQuery.error as Error).message}</div>
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">Ürünler</div>
                  <div className="mt-2 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
                    Dijital raporlarınızı ve satışa açık ürünlerinizi buradan yönetebilirsiniz.
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                      <div className="h-2 w-2 rounded-full bg-emerald-500" />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Aktif</div>
                      <div className="text-xl font-extrabold">{products.filter((p) => p.active).length}</div>
                    </div>
                  </div>

                  <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-slate-500/10 text-slate-600 dark:text-slate-300">
                      <div className="h-2 w-2 rounded-full bg-slate-400" />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Pasif</div>
                      <div className="text-xl font-extrabold">{products.filter((p) => !p.active).length}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    className="h-11 w-full rounded-2xl bg-slate-100 pl-10 pr-4 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:bg-slate-800 dark:text-slate-50"
                    placeholder="Ürün adı, SKU, kategori, etiket..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant={productFilter === "all" ? "default" : "outline"}
                    className={cn("rounded-full", productFilter !== "all" && "bg-white dark:bg-slate-950/40")}
                    onClick={() => setProductFilter("all")}
                  >
                    Tümü
                  </Button>
                  <Button
                    size="sm"
                    variant={productFilter === "active" ? "default" : "outline"}
                    className={cn("rounded-full", productFilter !== "active" && "bg-white dark:bg-slate-950/40")}
                    onClick={() => setProductFilter("active")}
                  >
                    Aktif
                  </Button>
                  <Button
                    size="sm"
                    variant={productFilter === "inactive" ? "default" : "outline"}
                    className={cn("rounded-full", productFilter !== "inactive" && "bg-white dark:bg-slate-950/40")}
                    onClick={() => setProductFilter("inactive")}
                  >
                    Pasif
                  </Button>

                  <div className="ml-0 flex items-center gap-2 lg:ml-2">
                    <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Sırala:</div>
                    <select
                      className="h-10 rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200"
                      value={productSort}
                      onChange={(e) => setProductSort(e.target.value as any)}
                    >
                      <option value="default">Varsayılan</option>
                      <option value="name-asc">Ad (A-Z)</option>
                      <option value="price-asc">Fiyat (Artan)</option>
                      <option value="price-desc">Fiyat (Azalan)</option>
                    </select>
                  </div>

                  <Button size="sm" className="rounded-full" onClick={() => navigate("/admin/numeroloji/urun/yeni")}
                  >
                    <span className="mr-1">+</span>
                    Yeni ürün
                  </Button>
                </div>
              </div>

              {productSaveMutation.isError ? (
                <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  {(productSaveMutation.error as Error).message}
                </div>
              ) : null}

              {productDeleteMutation.isError ? (
                <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  {(productDeleteMutation.error as Error).message}
                </div>
              ) : null}

              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {visibleProducts.map((p) => {
                  const badge = p.active ? "Aktif" : "Pasif";
                  const badgeClass = p.active ? "bg-emerald-500" : "bg-slate-500";
                  const primaryCategory = getPrimaryCategory(p.category);
                  const heroClass =
                    primaryCategory === "bundle"
                      ? "from-blue-600/30 via-indigo-600/20 to-slate-950/20"
                      : primaryCategory === "oil"
                        ? "from-amber-500/25 via-orange-500/15 to-slate-950/20"
                        : primaryCategory === "stone"
                          ? "from-emerald-500/20 via-teal-500/15 to-slate-950/20"
                          : "from-slate-500/20 via-slate-400/10 to-slate-950/20";

                  const cover =
                    (Array.isArray(p.imageUrls) && p.imageUrls.find((u) => typeof u === "string" && u.trim())) || p.imageUrl;

                  return (
                    <div
                      key={p.id}
                      className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-lg dark:border-slate-800 dark:bg-slate-950/40"
                    >
                      <div className={cn("relative aspect-[4/3] overflow-hidden bg-gradient-to-br", heroClass)}>
                        {cover ? <img src={cover} alt={p.name} className="absolute inset-0 h-full w-full object-cover" /> : null}
                        <div className="absolute inset-0 bg-black/10" />

                        <div
                          className={cn(
                            "absolute left-3 top-3 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white",
                            badgeClass,
                          )}
                        >
                          {badge}
                        </div>

                        <div className="absolute right-3 top-3 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            type="button"
                            className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-slate-700 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white dark:bg-slate-950/80 dark:text-slate-200 dark:ring-slate-800"
                            onClick={() => navigate(`/admin/numeroloji/urun/${encodeURIComponent(p.id)}`)}
                          >
                            <PencilRuler className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-rose-600 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white dark:bg-slate-950/80 dark:ring-slate-800"
                            disabled={productDeleteMutation.isPending}
                            onClick={() => {
                              const ok = confirm("Bu ürünü silmek istiyor musunuz?");
                              if (!ok) return;
                              productDeleteMutation.mutate(p.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        <div className="absolute bottom-3 left-3 rounded-2xl bg-white/70 px-3 py-2 text-xs font-semibold text-slate-900 backdrop-blur dark:bg-slate-950/60 dark:text-slate-50">
                          {formatCategoryLabel(p.category) || "Diğer"}
                        </div>
                      </div>

                      <div className="p-5">
                        <div className="text-base font-bold text-slate-900 dark:text-white">{p.name}</div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">SKU: {p.sku}</div>
                        <div className="mt-3 line-clamp-3 text-sm text-slate-600 dark:text-slate-300">{p.description}</div>

                        <div className="mt-4 flex items-center justify-between gap-3">
                          <div className="text-lg font-extrabold text-blue-600 dark:text-blue-300">{formatTry(p.priceTry)}</div>

                          <label className="relative inline-flex cursor-pointer items-center">
                            <input
                              type="checkbox"
                              className="peer sr-only"
                              checked={p.active}
                              disabled={productSaveMutation.isPending}
                              onChange={() =>
                                productSaveMutation.mutate({
                                  id: p.id,
                                  sku: p.sku,
                                  name: p.name,
                                  description: p.description,
                                  category: p.category,
                                  priceTry: p.priceTry,
                                  active: !p.active,
                                  tags: p.tags ?? [],
                                })
                              }
                            />
                            <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={() => navigate("/admin/numeroloji/urun/yeni")}
                  className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-200 bg-white p-6 text-slate-600 transition-colors hover:border-blue-500/50 hover:bg-blue-50/50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300 dark:hover:bg-slate-900/30"
                >
                  <div className="grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50">
                    <span className="text-2xl">+</span>
                  </div>
                  <div className="mt-3 text-sm font-bold">Yeni ürün ekle</div>
                  <div className="mt-1 text-center text-xs text-slate-500 dark:text-slate-400">Yeni bir rapor veya hizmet paketi oluştur.</div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
